import type { QuickReplyListFilter } from '../services/conversationQuickReplies';
import type {
    IteroOrdersFilterParams,
    IteroOrdersParams,
    IteroPatientsFilterParams,
    IteroPatientsParams
} from '../services/iteroBrowse';
import type { VirtualAppointmentOverviewQuery } from '../services/virtualAppointmentsOverview';
import type { HomeRangeKey } from './useBillingRange';

export const CONVERSATIONS_KEY = 'v2/conversations';
export const CONVERSATIONS_INFINITE_KEY = 'v2/conversations/infinite';
// The paged list's own segment, so a list-shaped cache write can't hit detail or home-mine.
export const CONVERSATIONS_PAGE_KEY = [CONVERSATIONS_KEY, 'page'] as const;
// Nested under CONVERSATIONS_KEY so an assign/mark-read invalidation refreshes
// a deep-linked panel too.
export const CONVERSATION_DETAIL_KEY = (patientId: string, status: string) =>
    [CONVERSATIONS_KEY, 'detail', patientId, status] as const;
export const CONVERSATIONS_FILTERS_KEY = 'v2/conversations/filters';
export const PATIENTS_KEY = 'v2/patients';
export const PATIENTS_FILTERS_KEY = 'v2/patients/filters';
// One cache entry per (patient, thread) so the external and internal threads'
// read state never collide.
export const CHAT_STATUS_KEY = (patientId: string, chatType: string) =>
    ['v1/patients', patientId, 'chat-status', chatType] as const;
export const PATIENT_NOTES_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'notes'] as const;
export const DX_REPORT_DRAFTS_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'clinical/dx-report/drafts'] as const;
export const PATIENT_ANALYSIS_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'analysis'] as const;
export const PATIENT_VIRTUAL_APPOINTMENTS_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'virtual-appointments'] as const;
export const PATIENT_VIRTUAL_APPOINTMENTS_LIST_KEY = (
    patientId: string,
    groupId: string | null,
    status: string | null
) => [...PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId), groupId, status] as const;
// Nested under the VA key so every VA invalidation also refreshes the round.
export const PATIENT_VIRTUAL_APPOINTMENT_ROUND_KEY = (patientId: string) =>
    [...PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId), 'round'] as const;
export const VIRTUAL_APPOINTMENT_TYPES_KEY = ['v2/virtual-appointments/types'] as const;
// Admin cross-patient VA overview; one base segment so a broad invalidation reaches list and detail.
export const VIRTUAL_APPOINTMENTS_OVERVIEW_KEY = 'v2/virtual-appointments';
export const VIRTUAL_APPOINTMENTS_OVERVIEW_LIST_KEY = (query: VirtualAppointmentOverviewQuery) =>
    [VIRTUAL_APPOINTMENTS_OVERVIEW_KEY, 'list', query] as const;
export const VIRTUAL_APPOINTMENTS_OVERVIEW_PAGE_KEY = (
    query: VirtualAppointmentOverviewQuery,
    page: number
) => [VIRTUAL_APPOINTMENTS_OVERVIEW_KEY, 'page', query, page] as const;
export const VIRTUAL_APPOINTMENT_OVERVIEW_DETAIL_KEY = (vaId: string) =>
    [VIRTUAL_APPOINTMENTS_OVERVIEW_KEY, 'detail', vaId] as const;
export const MANUFACTURER_STATUS_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'workflow/design/manufacturer/status'] as const;
export const FINAL_REVIEW_HAND_BACK_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'workflow/design/final-review/hand-back'] as const;
export const REFINEMENT_TARGET_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'workflow/in-treatment/refinement-needed/target'] as const;
export const ORTHOPULSE_ORDER_PREVIEW_KEY = (patientId: string, chargeId?: string) =>
    ['v2/patients', patientId, 'orthopulse-order-preview', chargeId ?? null] as const;

export const BILLING_CHARGES_KEY = 'v2/billing/charges';
export const BILLING_CHARGE_KEY = (id: string) => ['v2/billing/charges', id] as const;
// Nested under BILLING_CHARGES_KEY so a charge mutation's invalidation already
// reaches it by prefix match - no second invalidateQueries call needed.
export const BILLING_CHARGES_INFINITE_KEY = [BILLING_CHARGES_KEY, 'infinite'] as const;
export const BILLING_INVOICES_KEY = 'v2/billing/invoices';
// Distinct from BILLING_INVOICES_KEY's paged query - useInfiniteQuery's pages
// array is a different shape and needs its own cache entry.
export const BILLING_INVOICES_INFINITE_KEY = 'v2/billing/invoices/infinite';
export const BILLING_INVOICE_KEY = (id: string) => ['v2/billing/invoices', id] as const;
export const BILLING_ONE_TIME_DISCOUNTS_KEY = 'v2/billing/one-time-discounts';
// Nested for the same reason as BILLING_CHARGES_INFINITE_KEY - every mutation
// that invalidates one-time discounts already targets `[BILLING_ONE_TIME_DISCOUNTS_KEY]`.
export const BILLING_ONE_TIME_DISCOUNTS_INFINITE_KEY = [
    BILLING_ONE_TIME_DISCOUNTS_KEY,
    'infinite'
] as const;
export const BILLING_VENDORS_KEY = ['v2/billing/vendors'] as const;
// Nested under BILLING_VENDORS_KEY so a write invalidating the bare key reaches
// this list; not the reverse - the bare key's own readers need an exact call.
export const VENDORS_LIST_KEY = [...BILLING_VENDORS_KEY, 'list'] as const;
export const VENDOR_DETAIL_KEY = (id: string) => [...BILLING_VENDORS_KEY, 'detail', id] as const;
export const BILLING_PURCHASE_ORDERS_KEY = 'v2/billing/purchase-orders';
// Nested for the same reason as BILLING_CHARGES_INFINITE_KEY - a mutation that
// invalidates `[BILLING_PURCHASE_ORDERS_KEY]` already reaches both by prefix.
export const BILLING_PURCHASE_ORDERS_INFINITE_KEY = [
    BILLING_PURCHASE_ORDERS_KEY,
    'infinite'
] as const;
export const BILLING_PURCHASE_ORDER_KEY = (id: string) =>
    [BILLING_PURCHASE_ORDERS_KEY, 'detail', id] as const;
export const PRODUCTS_KEY = 'v2/products';
// Siblings of PRODUCTS_KEY, deliberately not children: every product mutation
// invalidates [PRODUCTS_KEY] by prefix, and neither of these changes with it.
export const PRODUCTS_TAXONOMY_KEY = ['v2/products/taxonomy'] as const;
export const PRODUCTS_IMAGES_KEY = 'v2/products/images';

// Shares the 'v2/practices' base segment with billing's usePracticeOptions /
// useSaveDiscountRule so a practice mutation there also invalidates this list.
export const ACCOUNTS_PRACTICES_KEY = 'v2/practices';
export const ACCOUNTS_PRACTICES_LIST_KEY = [ACCOUNTS_PRACTICES_KEY, 'list'] as const;
// Distinct from ACCOUNTS_PRACTICES_LIST_KEY's paged query - useInfiniteQuery's
// pages array is a different shape and needs its own cache entry.
export const ACCOUNTS_PRACTICES_INFINITE_KEY = [ACCOUNTS_PRACTICES_KEY, 'infinite'] as const;
export const ACCOUNTS_PRACTICES_FILTERS_KEY = 'v2/practices/filters';
export const PRACTICE_DETAIL_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'detail', practiceId] as const;
export const PRACTICE_STATS_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'stats', practiceId] as const;
// Global risk overlay (GET /practices/risk-statuses) - one shared list for every
// risk-coloring consumer, not per-practice; a PATCH of `risk` invalidates it.
export const PRACTICE_RISK_STATUSES_KEY = [ACCOUNTS_PRACTICES_KEY, 'risk-statuses'] as const;
export const PRACTICE_PATIENT_INSIGHTS_KEY = (practiceId: string, granularity: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'patient-insights', practiceId, granularity] as const;
// Grouped under the practices root, but a save invalidates
// ACCOUNTS_PRACTICES_LIST_KEY, not this root - so this key needs its own call.
export const PRACTICE_TIMELINE_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'timeline', practiceId] as const;
// Keyed on the debounced override inputs too - TanStack hashes key arrays
// structurally, so a new object identity per keystroke still refetches correctly.
export const SPARK_ACCOUNT_REQUEST_PREVIEW_KEY = (
    practiceId: string,
    overrides: { requestedUsername: string; doctorType: string; website: string }
) => [ACCOUNTS_PRACTICES_KEY, 'spark-account-request-preview', practiceId, overrides] as const;
// Base key lets a sync mutation invalidate a practice's `live: false` and
// `live: true` cache entries in one call.
export const SPARK_CREDS_STATUS_BASE_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'spark-creds-status', practiceId] as const;
export const SPARK_CREDS_STATUS_KEY = (practiceId: string, live: boolean) =>
    [...SPARK_CREDS_STATUS_BASE_KEY(practiceId), live] as const;
export const PRACTICE_DM_DOCTOR_SEARCH_KEY = (practiceId: string, search: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'dm-doctor-search', practiceId, search] as const;
export const PRACTICE_DM_PRACTICE_SEARCH_KEY = (practiceId: string, search: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'dm-practice-search', practiceId, search] as const;
const ITERO_CONNECTION_BASE_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'itero', practiceId] as const;
export const ITERO_STATUS_KEY = (practiceId: string) =>
    [...ITERO_CONNECTION_BASE_KEY(practiceId), 'status'] as const;
export const ITERO_CLINICS_KEY = (practiceId: string) =>
    [...ITERO_CONNECTION_BASE_KEY(practiceId), 'clinics'] as const;
// Keyed by clinic so a reconnect to another clinic never shows the old clinic's records.
const ITERO_BROWSE_KEY = (practiceId: string, clinicId: number) =>
    [...ITERO_CONNECTION_BASE_KEY(practiceId), 'browse', clinicId] as const;
export const ITERO_PATIENTS_KEY = (
    practiceId: string,
    clinicId: number,
    params: IteroPatientsParams
) => [...ITERO_BROWSE_KEY(practiceId, clinicId), 'patients', params] as const;
export const ITERO_PATIENTS_INFINITE_KEY = (
    practiceId: string,
    clinicId: number,
    filters: IteroPatientsFilterParams
) => [...ITERO_BROWSE_KEY(practiceId, clinicId), 'patients', 'infinite', filters] as const;
export const ITERO_ORDERS_KEY = (
    practiceId: string,
    clinicId: number,
    filters: IteroOrdersFilterParams
) => [...ITERO_BROWSE_KEY(practiceId, clinicId), 'orders', filters] as const;
export const ITERO_ORDERS_PAGE_KEY = (
    practiceId: string,
    clinicId: number,
    params: IteroOrdersParams
) => [...ITERO_BROWSE_KEY(practiceId, clinicId), 'orders', 'page', params] as const;
export const ITERO_PATIENT_ORDERS_KEY = (
    practiceId: string,
    clinicId: number,
    chartNumber: string
) => [...ITERO_BROWSE_KEY(practiceId, clinicId), 'patient-orders', chartNumber] as const;
export const ITERO_ORDER_KEY = (practiceId: string, clinicId: number, rxId: string) =>
    [...ITERO_BROWSE_KEY(practiceId, clinicId), 'order', rxId] as const;
export const ITERO_RX_KEY = (practiceId: string, clinicId: number, rxId: string) =>
    [...ITERO_ORDER_KEY(practiceId, clinicId, rxId), 'rx'] as const;
export const ITERO_SCAN_IMAGES_KEY = (
    practiceId: string,
    clinicId: number,
    rxId: string,
    assetType: string
) => [...ITERO_ORDER_KEY(practiceId, clinicId, rxId), 'scan-images', assetType] as const;
export const ITERO_SCAN_MESHES_KEY = (practiceId: string, clinicId: number, rxId: string) =>
    [...ITERO_ORDER_KEY(practiceId, clinicId, rxId), 'scan-meshes'] as const;

// Accounts > Orthodontists - the parallel org list/detail, under its own base
// segment.
export const ACCOUNTS_ORTHODONTISTS_KEY = 'v2/orthodontist';
// Prefixes useOrthodontists'/useInfiniteOrthodontists' own list/infinite query
// keys, so a create invalidates both regardless of page/sort/filter.
export const ACCOUNTS_ORTHODONTISTS_LIST_KEY = [ACCOUNTS_ORTHODONTISTS_KEY, 'list'] as const;
export const ACCOUNTS_ORTHODONTISTS_INFINITE_KEY = [
    ACCOUNTS_ORTHODONTISTS_KEY,
    'infinite'
] as const;
export const ORTHODONTIST_DETAIL_KEY = (orthodontistId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'detail', orthodontistId] as const;
export const ORTHODONTIST_USERS_KEY = (orthodontistId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'users', orthodontistId] as const;
// GET /orthodontist/by-user/:userId - one user's org memberships, keyed
// separately from ORTHODONTIST_USERS_KEY since it's indexed by user, not org.
export const ORTHODONTIST_USER_MEMBERSHIPS_KEY = (userId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'memberships', userId] as const;
// Accounts > Orthodontist detail > Details > Feature settings section.
export const ORTHODONTIST_FEATURE_FLAGS_KEY = (orthodontistId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'feature-flags', orthodontistId] as const;
// Accounts > Orthodontist detail > Preferences & protocols tab.
export const ORTHODONTIST_PREFERENCES_KEY = (orthodontistId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'preferences', orthodontistId] as const;
export const ORTHODONTIST_PROTOCOLS_KEY = (orthodontistId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'protocols', orthodontistId] as const;
export const PRACTICE_ORTHODONTIST_PROTOCOLS_KEY = (practiceId: string) =>
    [ACCOUNTS_ORTHODONTISTS_KEY, 'protocols-by-practice', practiceId] as const;

export const COMMUNICATIONS_KEY = 'v2/communications';
export const COMMUNICATION_DETAIL_KEY = (id: string) => [COMMUNICATIONS_KEY, 'detail', id] as const;
export const COMMUNICATION_CATEGORY_COUNTS_KEY = (practiceId: string, days: number) =>
    [COMMUNICATIONS_KEY, 'category-counts', practiceId, days] as const;
// A distinct key rather than COMMUNICATION_CATEGORY_COUNTS_KEY with an empty
// id, so the global counts and a per-id set never collide in cache.
export const COMMUNICATION_CATEGORY_COUNTS_GLOBAL_KEY = (days: number) =>
    [COMMUNICATIONS_KEY, 'category-counts', 'global', days] as const;
// Nested under COMMUNICATIONS_KEY: keys match element-wise, so a bare string
// would leave the stat cards stale while the table below them refreshed.
export const COMMUNICATIONS_SUMMARY_KEY = [COMMUNICATIONS_KEY, 'summary'] as const;
// One shared key per granularity - the overview's three insight cards are all
// backed by a single query.
export const COMMUNICATIONS_INSIGHTS_KEY = (granularity: string) =>
    [COMMUNICATIONS_KEY, 'insights', granularity] as const;

// Admin home dashboard. Team stats are keyed by range preset and its days, so a same-day
// revisit hits the cache but a new day or month refetches; my-cases by page size.
export const HOME_TEAM_STATS_KEY = (range: HomeRangeKey, fromDay: string | null, toDay: string) =>
    ['v2/admin/home', 'team-stats', range, fromDay, toDay] as const;
export const HOME_MY_CASES_BASE_KEY = ['v2/admin/home', 'my-cases'] as const;
export const HOME_MY_CASES_KEY = (limit: number) => [...HOME_MY_CASES_BASE_KEY, limit] as const;
// Nested under CONVERSATIONS_KEY so the paged list's invalidations refresh the
// Home card. The server resolves the caller, so only the limit varies.
export const HOME_MY_CONVERSATIONS_KEY = (limit: number) =>
    [CONVERSATIONS_KEY, 'home-mine', limit] as const;

// Accounts > Users (global admin directory).
export const ACCOUNTS_USERS_KEY = 'v2/users';

// PRACTICE_USERS_KEY(id) prefixes the paged/infinite keys below it;
// PRACTICE_USERS_BASE_KEY prefixes every practice's list.
export const PRACTICE_USERS_BASE_KEY = [ACCOUNTS_PRACTICES_KEY, 'users'] as const;
export const PRACTICE_USERS_KEY = (practiceId: string) =>
    [...PRACTICE_USERS_BASE_KEY, practiceId] as const;
export const PRACTICE_USERS_PAGED_KEY = (
    practiceId: string,
    page: number,
    limit: number,
    search: string
) => [...PRACTICE_USERS_KEY(practiceId), 'paged', page, limit, search] as const;
export const PRACTICE_USERS_INFINITE_KEY = (practiceId: string, limit: number, search: string) =>
    [...PRACTICE_USERS_KEY(practiceId), 'infinite', limit, search] as const;

// Accounts > Users > edit drawer > Notifications section - admin editing
// another user's settings via /api/v2/users/:userId/settings.
export const USER_SETTINGS_KEY = (userId: string) =>
    [ACCOUNTS_USERS_KEY, 'settings', userId] as const;

// Accounts > Practice detail > Details > Files section.
export const PRACTICE_DOCUMENTS_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'documents', practiceId] as const;

// Events admin (admin-only /api/v2/events - see services/events.ts).
export const EVENTS_KEY = 'v2/events';
export const EVENT_DETAIL_KEY = (id: string) => [EVENTS_KEY, 'detail', id] as const;
export const EVENT_SIGNUPS_KEY = (id: string, page: number, limit: number) =>
    [EVENTS_KEY, 'signups', id, page, limit] as const;
// Keyed by the draft itself, not by an event id: the preview is composed from
// unsaved form state, and a new draft is a new key.
export const EVENT_EMAIL_PREVIEW_KEY = (fingerprint: string) =>
    [EVENTS_KEY, 'email-preview', fingerprint] as const;

// The global list only backs the "known feature names" suggestions, not the row
// list, so it carries a longer staleTime than the per-practice one.
export const PRACTICE_FEATURE_FLAGS_KEY = (practiceId: string) =>
    [ACCOUNTS_PRACTICES_KEY, 'feature-flags', practiceId] as const;
export const PRACTICE_FEATURE_FLAGS_ALL_KEY = [
    ACCOUNTS_PRACTICES_KEY,
    'feature-flags',
    'all'
] as const;

// Access section (the IAM console) - nested under one base segment so a
// broad invalidation can hit the users list and any open user detail at once.
export const IAM_KEY = 'v2/iam';
// GET /iam/me permissions; invalidated after grant create/revoke.
export const IAM_ME_KEY = [IAM_KEY, 'me'] as const;
export const IAM_CATALOG_KEY = [IAM_KEY, 'catalog'] as const;
export const IAM_USERS_BASE_KEY = [IAM_KEY, 'users'] as const;
export const IAM_USER_KEY = (userId: string) => [...IAM_USERS_BASE_KEY, userId] as const;
// GET /iam/grantable - code-defined list, nothing invalidates it today.
export const IAM_GRANTABLE_KEY = [IAM_KEY, 'grantable'] as const;

// Backed by /api/v2/roles - "teams" is the product name for what the API calls
// roles. One base segment so a broad invalidation reaches every branch at once.
export const TEAMS_KEY = 'v2/roles';
export const TEAMS_LIST_KEY = (scope?: string) => [TEAMS_KEY, 'list', scope ?? 'all'] as const;
export const TEAM_MEMBERS_KEY = (teamId: string) => [TEAMS_KEY, 'members', teamId] as const;
// The candidate pool for a scope (GET /v2/users?scope=...) - separate from
// 'members' since it is not the roster and no membership mutation touches it.
export const TEAM_USERS_KEY = (scope: string) => [TEAMS_KEY, 'users', scope] as const;

// Work Queue toolbar's read-only, all-practices appointments calendar - one
// fixed ±6-month window, keyed by its bounds so a fresh "today" refetches.
export const ADMIN_APPOINTMENTS_BASE_KEY = ['v2/admin/appointments'] as const;
export const ADMIN_APPOINTMENTS_KEY = (from: string, to: string) =>
    [...ADMIN_APPOINTMENTS_BASE_KEY, from, to] as const;
export const ADMIN_APPOINTMENT_DETAIL_KEY = (appointmentId: string) =>
    [...ADMIN_APPOINTMENTS_BASE_KEY, 'detail', appointmentId] as const;
// Toolbar badge - keyed by `from`/`to` so it stays bounded to the same window.
export const ADMIN_APPOINTMENTS_COUNT_KEY = (from: string, to: string) =>
    [...ADMIN_APPOINTMENTS_BASE_KEY, 'count', from, to] as const;
// Not under CONVERSATIONS_KEY: thread invalidations must not refetch the templates.
export const CONVERSATION_QUICK_REPLIES_KEY = ['v2/conversations/quick-replies'] as const;
export const CONVERSATION_QUICK_REPLIES_LIST_KEY = (filter: QuickReplyListFilter) =>
    [...CONVERSATION_QUICK_REPLIES_KEY, filter.chatType ?? 'all'] as const;
export const DM_QUICK_REPLIES_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'dm', 'quick-replies'] as const;
// Under the scans list key, so a scans invalidation also refreshes the rejected list.
export const DM_REJECTED_SCANS_KEY = (patientId: string) =>
    ['v2/patients', patientId, 'monitoring', 'scans', 'rejected'] as const;
// Every per-patient key starts with this one, so invalidate it with exact: true
// unless every per-patient query should refetch too.
export const PATIENT_CARD_KEY = (patientId: string | undefined) =>
    [PATIENTS_KEY, patientId] as const;
export const DM_NOTES_KEY = (patientId: string | undefined) =>
    [...PATIENT_CARD_KEY(patientId), 'dm', 'notes'] as const;
export const DM_NOTES_LIVE_KEY = (patientId: string | undefined) =>
    [...DM_NOTES_KEY(patientId), 'live'] as const;
// Monitoring > Notifications: open DM notifications grouped per patient.
export const DM_NOTIFICATIONS_KEY = 'v2/admin/monitoring/notifications';
// Monitoring > Messages: patients with unreviewed DM patient messages.
export const DM_MESSAGES_INBOX_KEY = 'v2/admin/monitoring/messages';

interface DmInboxKeyFilters {
    sort: string;
    order: string;
    levels?: readonly string[];
    practiceIds?: readonly string[];
    assignees?: readonly string[];
    versions?: readonly string[];
    status?: string;
    search?: string;
}

// Both monitoring inboxes' list filters, so Open and Closed never share a cache entry.
export function dmInboxFilterKeyParts(filters: DmInboxKeyFilters): string[] {
    return [
        filters.sort,
        filters.order,
        filters.levels?.join(',') ?? '',
        filters.practiceIds?.join(',') ?? '',
        filters.assignees?.join(',') ?? '',
        filters.versions?.join(',') ?? '',
        filters.status ?? 'open',
        filters.search ?? ''
    ];
}
// Monitoring > Overview: DM workload totals across every patient.
export const MONITORING_OVERVIEW_KEY = ['v2/admin/monitoring/overview'] as const;
// Every sidebar badge in one poll; writes that move a badge call invalidateSidebarCounts.
export const SIDEBAR_COUNTS_KEY = ['v2/admin/sidebar-counts'] as const;
// Keyed by tz too: the backend buckets days in the browser's zone.
export const MONITORING_OVERVIEW_HISTORY_KEY = (range: string, tz: string) =>
    [...MONITORING_OVERVIEW_KEY, 'history', range, tz] as const;
// One entry per kind and bucket serves every card of that kind; each reads its own category.
export const MONITORING_OVERVIEW_TRAILING_KEY = (kind: string, bucket: string, tz: string) =>
    [...MONITORING_OVERVIEW_KEY, kind, bucket, tz] as const;
export const MONITORING_OVERVIEW_VA_MIX_KEY = (team: string) =>
    [...MONITORING_OVERVIEW_KEY, 'virtual-appointments/mix', team] as const;

// Patient Files tab assets (GET /api/v1/patients/:id/cloud-files).
export const PATIENT_ASSETS_KEY = (patientId: string) =>
    ['v1/patients', patientId, 'assets'] as const;
// Root segment of the Work Queue keys that useWorkQueue.ts still writes inline.
export const WORK_QUEUE_KEY = 'work-queue';
